import { AuditAction, AuditResourceType } from '_lib/api';
import { useAppDispatch } from '_store';
import auditReducer from '_store/audit/auditReducers';
import { auditFiltersSelector } from '_store/audit/selectors';
import { useLocalization } from '@fluent/react';
import { Button } from '@material-ui/core';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import DatePicker from '../../../items/DatePicker';
import SelectItem, { Option } from '../../../items/SelectItem';
import TextFieldItem from '../../../items/TextFieldItem';
import styles from './styles';

const toIso = (date: Date | null): string | null => (date ? date.toISOString() : null);
const fromIso = (iso: string | null): Date | null => (iso ? new Date(iso) : null);

export const AuditListFilters: FC = () => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const filters = useSelector(auditFiltersSelector);

  const setFilters = (patch: Parameters<typeof auditReducer.actions.setFilters>[0]) =>
    dispatch(auditReducer.actions.setFilters(patch));

  const anyOption: Option = { label: l10n.getString('audit-filter-any'), value: '' };
  const actionOptions: Option[] = [
    anyOption,
    ...Object.values(AuditAction).map((value) => ({
      label: l10n.getString(`audit-action-${value}`),
      value,
    })),
  ];
  const resourceTypeOptions: Option[] = [
    anyOption,
    ...Object.values(AuditResourceType).map((value) => ({ label: value, value })),
  ];

  return (
    <div className={classes.filters}>
      <div className={classes.filterField}>
        <SelectItem
          text={l10n.getString('audit-filter-action')}
          options={actionOptions}
          value={filters.action}
          onChange={(event) => setFilters({ action: event.target.value as AuditAction | '' })}
        />
      </div>

      <div className={classes.filterField}>
        <SelectItem
          text={l10n.getString('audit-filter-resource-type')}
          options={resourceTypeOptions}
          value={filters.resourceType}
          onChange={(event) =>
            setFilters({ resourceType: event.target.value as AuditResourceType | '' })
          }
        />
      </div>

      <div className={classes.filterField}>
        <DatePicker
          label={l10n.getString('audit-filter-from')}
          value={fromIso(filters.fromDate)}
          disablePast={false}
          onChange={(date) => setFilters({ fromDate: toIso(date) })}
          tooltip={l10n.getString('audit-filter-from-tp')}
        />
      </div>

      <div className={classes.filterField}>
        <DatePicker
          label={l10n.getString('audit-filter-to')}
          value={fromIso(filters.toDate)}
          disablePast={false}
          onChange={(date) => setFilters({ toDate: toIso(date) })}
          tooltip={l10n.getString('audit-filter-to-tp')}
        />
      </div>

      <div className={classes.filterField}>
        <TextFieldItem
          text={l10n.getString('audit-filter-username')}
          value={filters.username}
          onChange={(event) => setFilters({ username: event.target.value })}
          tooltip={l10n.getString('audit-filter-username-tp')}
        />
      </div>

      <div className={classes.filterField}>
        <TextFieldItem
          text={l10n.getString('audit-filter-resource-id')}
          value={filters.resourceId}
          onChange={(event) => setFilters({ resourceId: event.target.value })}
          tooltip={l10n.getString('audit-filter-resource-id-tp')}
        />
      </div>

      <div className={classes.filterActions}>
        <Button
          variant="text"
          color="primary"
          className={classes.resetButton}
          onClick={() => dispatch(auditReducer.actions.resetFilters())}
        >
          {l10n.getString('audit-filter-reset')}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(AuditListFilters);
